/**
 * Firestore: merchants/{merchantId}/rewards/{rewardId}
 * Schema matches server / mobile expectations (see Firestore console):
 * cost_points, cost_type, cost_visits, created_at, currency, django_updated_at,
 * firebase_uid, merchant_id, reward_id, reward_type, status
 */
import { deleteDoc, doc, getDoc, getFirestore, setDoc } from "firebase/firestore";
import { firebaseApp } from "../firebase/firebaseClient";

const INIT_DOC_ID = "_init";

function getDb() {
  return getFirestore(firebaseApp);
}

const programRewardStorageKey = (merchantId) => `klerk_program_reward_doc_id:${merchantId}`;

function rewardTypeSlug(text) {
  const s = String(text || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return s || "reward";
}

/**
 * Resolve Square / dashboard merchant id for the signed-in user.
 */
export async function fetchMerchantIdForCurrentUser(axiosInstance) {
  const ls = localStorage.getItem("merchant_id");
  if (ls) return String(ls).trim();
  try {
    const { data } = await axiosInstance.get("/api/user-businesses/");
    const row = Array.isArray(data) ? data[0] : null;
    if (row?.merchant_id) return String(row.merchant_id).trim();
  } catch {
    // ignore
  }
  return "";
}

/**
 * Placeholder document so the rewards subcollection exists (similar to users/{uid}/.../_init).
 */
export async function ensureMerchantRewardsSubcollection(merchantId) {
  const mid = String(merchantId || "").trim();
  if (!mid) return;
  try {
    const ref = doc(getDb(), "merchants", mid, "rewards", INIT_DOC_ID);
    await setDoc(
      ref,
      { createdAt: new Date().toISOString() },
      { merge: true }
    );
  } catch (e) {
    console.warn("[merchantRewardsFirestore] ensureMerchantRewardsSubcollection:", e);
  }
}

/**
 * Visit-based program reward from dashboard (loyalty settings / home punch card).
 * Reuses one document id per merchant (localStorage) so updates do not create duplicates.
 */
export async function upsertMerchantProgramReward(merchantId, { visits, rewardType, rewardDescription }) {
  const mid = String(merchantId || "").trim();
  if (!mid) return;

  const v = Math.max(1, Number(visits) || 1);
  const rt = String(rewardType || rewardDescription || "").trim();
  const rd = String(rewardDescription || rewardType || "").trim();
  const slug = rewardTypeSlug(rt || rd);

  try {
    const db = getDb();
    let rewardDocId = localStorage.getItem(programRewardStorageKey(mid));
    if (!rewardDocId) {
      rewardDocId = crypto.randomUUID();
      localStorage.setItem(programRewardStorageKey(mid), rewardDocId);
    }

    const ref = doc(db, "merchants", mid, "rewards", rewardDocId);
    const existing = await getDoc(ref);
    const now = new Date().toISOString();
    const prior = existing.exists() ? existing.data() : null;
    const createdAt = prior?.created_at ? String(prior.created_at) : now;

    const payload = {
      reward_id: rewardDocId,
      merchant_id: mid,
      reward_type: slug,
      cost_type: "VISITS",
      currency: "VISITS",
      cost_points: 0,
      cost_visits: v,
      firebase_uid: null,
      status: "WAITING",
      created_at: createdAt,
      django_updated_at: now,
    };

    await setDoc(ref, payload, { merge: true });
  } catch (e) {
    console.warn("[merchantRewardsFirestore] upsertMerchantProgramReward:", e);
  }
}

/**
 * Mirror a reward from API (create/update response) into the merchant subcollection.
 */
export async function syncMerchantRewardDocument(merchantId, reward) {
  const mid = String(merchantId || "").trim();
  const rid = String(reward?.reward_id ?? reward?.id ?? "").trim();
  if (!mid || !rid) return;

  try {
    const db = getDb();
    const ct = String(reward.cost_type || "POINTS").toUpperCase() === "VISITS" ? "VISITS" : "POINTS";
    const cpoints = parseInt(reward.cost_points, 10);
    const cvisits = parseInt(reward.cost_visits, 10);
    const now = new Date().toISOString();

    const ref = doc(db, "merchants", mid, "rewards", rid);
    const existing = await getDoc(ref);
    const prior = existing.exists() ? existing.data() : null;
    const createdAt = prior?.created_at
      ? String(prior.created_at)
      : reward.created_at
        ? String(reward.created_at)
        : now;
    const djangoUpdated =
      reward.django_updated_at != null
        ? String(reward.django_updated_at)
        : reward.updated_at
          ? String(reward.updated_at)
          : now;

    const payload = {
      reward_id: rid,
      merchant_id: mid,
      reward_type: String(reward.reward_type || "").trim() || "reward",
      cost_type: ct,
      currency: String(reward.currency || ct)
        .trim() || ct,
      cost_points: Number.isFinite(cpoints) ? cpoints : 0,
      cost_visits: Number.isFinite(cvisits) ? cvisits : 0,
      firebase_uid: reward.firebase_uid != null && reward.firebase_uid !== "" ? reward.firebase_uid : null,
      status: String(reward.status || "WAITING").toUpperCase() || "WAITING",
      created_at: createdAt,
      django_updated_at: djangoUpdated,
    };

    await setDoc(ref, payload, { merge: true });
  } catch (e) {
    console.warn("[merchantRewardsFirestore] syncMerchantRewardDocument:", e);
  }
}

export async function deleteMerchantRewardDocument(merchantId, rewardId) {
  const mid = String(merchantId || "").trim();
  const rid = String(rewardId || "").trim();
  if (!mid || !rid) return;
  try {
    await deleteDoc(doc(getDb(), "merchants", mid, "rewards", rid));
  } catch (e) {
    console.warn("[merchantRewardsFirestore] deleteMerchantRewardDocument:", e);
  }
}
